import { createContext, useContext, useRef } from 'react';
import { createStore, useStore } from 'zustand';
import { StreamResponse } from '../types';
import { pushDataLayer } from './gtm.service';
import { useLlm } from './useLlm';

const LLM_MINIMIZED_KEY = 'llm-minimized';

function getPersistedMinimized(): boolean {
  try {
    return sessionStorage.getItem(LLM_MINIMIZED_KEY) === 'true';
  } catch {
    return false;
  }
}

function persistMinimized(minimized: boolean) {
  try {
    sessionStorage.setItem(LLM_MINIMIZED_KEY, String(minimized));
  } catch {
    // error
  }
}

const USE_SCROLL_INTO_VIEW = false;

export type ScrollToOptions = {
  offset?: number;
  immediate?: boolean;
  lock?: boolean;
};

export type ScrollerRef = {
  scrollTo: (
    target: string | number | HTMLElement,
    options?: ScrollToOptions
  ) => void;
  stop: () => void;
  start: () => void;
  on(event: string, callback: Function): any;
  off(event: string, callback: Function): any;
};

export type LlmPortal = {
  selector: string;
  replace: boolean;
};

export type LlmViewOptions = {
  dismissable?: boolean;
  embedded?: boolean;
  idleTime?: number;
  imperative?: boolean;
  opened?: boolean;
  portal?: Partial<LlmPortal>;
  preview?: boolean;
  skipCustomIntro?: boolean;
  threadId?: string;
};

export type LlmViewProps = {
  opened: boolean;
  dismissable: boolean;
  imperative: boolean;
  idleTime: number;
  skipCustomIntro: boolean;
  embedded: boolean;
  portal: LlmPortal;
  introed: boolean;
  hidden: boolean;
  fixed: boolean;
  idle: boolean;
  visible: boolean;
  direction: number;
  minimized: boolean;
  scrollable: boolean;
};

export type LlmViewActions = {
  initView: () => Promise<void>;
  initIntersection: (element: Element | null) => void;
  send: (
    message: string,
    onMessage?: (response: StreamResponse) => void,
    onEnd?: (response: StreamResponse) => void
  ) => Promise<void>;
  open: () => void;
  isOpened: () => boolean;
  setOpened: (opened: boolean) => void;
  setIntroed: (introed: boolean) => void;
  setVisible: (visible: boolean) => void;
  setMinimized: (minimized: boolean) => void;
  setScrollable: (scrollable: boolean) => void;
  setScroller: (scroller?: ScrollerRef) => void;
  scrollerStop: () => void;
  scrollerStart: () => void;
  scrollToBottom: () => void;
};

export type LlmViewState = LlmViewProps & {
  actions: LlmViewActions;
};

export type LlmViewStore = ReturnType<typeof createLlmViewStore>;

const createLlmViewStore = ({
  threadId,
  dismissable = true,
  imperative = false,
  skipCustomIntro = false,
  idleTime = 1000 * 30,
  opened,
  embedded,
  portal,
  send,
  setEmbed,
}: LlmViewOptions & {
  send: (
    message: string,
    onPrompt?: () => void,
    onMessage?: (response: StreamResponse) => void,
    onEnd?: (response: StreamResponse) => void
  ) => Promise<void>;
  setEmbed: (element: Element, active: boolean) => void;
}) => {
  const props: LlmViewProps = {
    opened:
      opened != null
        ? opened
        : embedded != null || threadId != null
          ? true
          : false,
    dismissable: embedded ? false : dismissable,
    imperative,
    skipCustomIntro: skipCustomIntro,
    embedded: embedded || false,
    portal: {
      selector: '[data-llm-result]',
      replace: true,
      ...portal,
    },
    introed: skipCustomIntro ? true : false,
    idleTime: idleTime,
    hidden: false,
    fixed: false,
    idle: true,
    visible: false,
    minimized: getPersistedMinimized(),
    scrollable: false,
    direction: 0,
  };

  let scroller_: ScrollerRef | undefined = undefined;

  const useStore = createStore<LlmViewState>((set, get) => {
    const onScrollerScroll = (event: any) => {
      const { direction, targetScroll, limit } = event;
      // console.log('onScrollerScroll', direction, targetScroll, limit);
      const scrolled = targetScroll > 100;
      const fixed = direction == -1 && scrolled;
      const hidden = direction == 1 && scrolled;
      const scrollable = limit - targetScroll > 100;
      const state = get();
      if (
        state.hidden !== hidden ||
        state.fixed !== fixed ||
        state.direction !== direction ||
        state.scrollable !== scrollable
      ) {
        set((state) => ({ hidden, fixed, direction, scrollable }));
      }
      // console.log('onScrollerScroll', event, event.progress, event.direction);
    };

    let to: number;

    const clearIdleTimeout = () => {
      if (to) {
        clearTimeout(to);
      }
      set(() => ({ idle: false }));
    };

    const setIdleTimeout = () => {
      clearIdleTimeout();
      const { idleTime } = get();
      return setTimeout(() => {
        // console.log('idle');
        set(() => ({ idle: true }));
      }, idleTime) as unknown as number;
    };

    return {
      ...props,
      actions: {
        initView: async () => {
          // const state = get();
          const handleIdleTimes = () => {
            to = setIdleTimeout();
            let lastScrollTop: number = 0;
            const onScroll = () => {
              const scrollTop = window.scrollY || document.documentElement.scrollTop;
              let direction = 0;
              if (scrollTop > lastScrollTop) {
                direction = 1;
              } else if (scrollTop < lastScrollTop) {
                direction = -1;
              }
              lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
              if (direction === 1) {
                to = setIdleTimeout();
              }
            };
            const onResize = () => {
              if (
                document.scrollingElement &&
                document.scrollingElement.scrollHeight ===
                  (document.scrollingElement as HTMLElement).offsetHeight
              ) {
                clearIdleTimeout();
              } else {
                setIdleTimeout();
              }
              lastScrollTop = 0;
            };
            /*
            if ('ResizeObserver' in window) {
              const resizeObserver = new ResizeObserver((entries) => {
                onScroll();
              });
              resizeObserver.observe(document.scrollingElement as HTMLElement);
            }
            */
            window.addEventListener('scroll', onScroll);
            window.addEventListener('resize', onResize);
            window.addEventListener('wheel', onResize);
            window.addEventListener('click', onResize);
          };
          setTimeout(() => {
            set(() => ({ idle: false }));
            handleIdleTimes();
          }, 2000);
        },
        initIntersection: (element: Element | null) => {
          // console.log('initIntersection', element);
          const handleIntersection = () => {
            // console.log('handleIntersection', element);
            if (!element) {
              return;
            }
            if ('IntersectionObserver' in window) {
              const observer = new IntersectionObserver(
                (entries, observer) => {
                  entries.forEach((entry) => {
                    // console.log('IntersectionObserver', entry.target, entry.isIntersecting);
                    const state = get();
                    state.actions.setVisible(entry.isIntersecting);
                    if (embedded) {
                      setEmbed(entry.target, entry.isIntersecting);
                    }
                  });
                },
                {
                  root: document,
                  rootMargin: '50px',
                  threshold: [0.01, 0.99],
                }
              );
              observer.observe(element);
            } else {
              const state = get();
              state.actions.setVisible(true);
            }
          };
          handleIntersection();
        },
        send: async (prompt, onMessage, onEnd) => {
          await send(
            prompt,
            () => {
              persistMinimized(false);
              set({
                skipCustomIntro: true,
                opened: true,
                minimized: false,
              });
              setTimeout(() => {
                /*
              const state = get();
              state.actions.scrollToBottom();
              */
                if (USE_SCROLL_INTO_VIEW && scroller_) {
                  // console.log('useLlmView.onSent', scroller_);
                  const userMessages = Array.from(
                    document.querySelectorAll('[data-message-role="user"]')
                  );
                  if (userMessages.length > 0) {
                    const lastUserMessage = userMessages.pop() as HTMLElement;
                    lastUserMessage.scrollIntoView({
                      behavior: 'instant',
                      block: 'start',
                      inline: 'start',
                    });
                    /*
                  scroller_.scrollTo(lastUserMessage, {
                    immediate: true,
                  });
                  */
                  }
                }
              }, 50);
            },
            (response) => {
              if (typeof onMessage === 'function') {
                onMessage(response);
              }
            },
            (response) => {
              if (typeof onEnd === 'function') {
                onEnd(response);
              }
            }
          );
        },
        open: () => {
          setIdleTimeout();
          set((state) => {
            const opened = !state.opened;
            pushDataLayer({
              event: opened ? 'llm_open' : 'llm_close',
            });
            persistMinimized(false);
            return { opened, minimized: false };
          });
        },
        isOpened: () => {
          const { opened } = get();
          return opened;
        },
        setOpened: (opened: boolean) => {
          setIdleTimeout();
          set((state) => {
            pushDataLayer({
              event: opened ? 'llm_open' : 'llm_close',
            });
            return { opened };
          });
        },
        setIntroed: (introed: boolean) => {
          const state = get();
          if (introed) {
            state.actions.scrollerStart();
          } else {
            state.actions.scrollerStop();
          }
          set((state) => ({ introed }));
        },
        setMinimized: (minimized: boolean) => {
          persistMinimized(minimized);
          set((state) => ({ minimized }));
        },
        setVisible: (visible: boolean) => set((state) => ({ visible })),
        setScrollable: (scrollable: boolean) =>
          set((state) => ({ scrollable })),
        setScroller: (scroller?: ScrollerRef) => {
          if (scroller_ !== scroller) {
            if (scroller_) {
              scroller_.off('scroll', onScrollerScroll);
            }
            scroller_ = scroller;
            if (scroller) {
              scroller.on('scroll', onScrollerScroll);
            }
          }
        },
        scrollerStop: () => {
          if (scroller_) {
            scroller_.stop();
          }
        },
        scrollerStart: () => {
          if (scroller_) {
            scroller_.start();
          }
        },
        scrollToBottom: () => {
          if (scroller_) {
            scroller_.scrollTo('bottom', {
              immediate: true,
            });
          }
        },
      },
    };
  });
  return useStore;
};

export const LlmViewContext = createContext<LlmViewStore | null>(null);

type LlmViewProviderProps = React.PropsWithChildren<LlmViewOptions>;

function LlmViewProvider({ children, ...props }: LlmViewProviderProps) {
  const { send, setEmbed } = useLlm(state => state.actions);
  const storeRef = useRef<LlmViewStore>(undefined);
  if (!storeRef.current) {
    storeRef.current = createLlmViewStore({ ...props, send, setEmbed });
  }
  return (
    storeRef.current && (
      <LlmViewContext.Provider value={storeRef.current}>
        {children}
      </LlmViewContext.Provider>
    )
  );
}

function useLlmView<T>(selector: (state: LlmViewState) => T): T {
  const store = useContext(LlmViewContext);
  if (!store) throw new Error('Missing LlmContext.Provider in the tree');
  return useStore(store, selector);
}

export { LlmViewProvider, useLlmView };

